import { useState } from "react";
import { motion } from "framer-motion";
import {
  Scissors,
  Sparkles,
  Eye,
  Brush,
  Syringe,
  SprayCan,
  Palette,
  HeartHandshake,
  Leaf,
  MapPin,
  Clock,
  Star,
} from "lucide-react";
import { fadeUp, stagger, viewportOnce, EASE } from "@/lib/motion";
import { goCustomer } from "@/lib/links";
import { trackCategoryClick } from "@/lib/analytics";
import ConsumerWaitlistForm from "@/components/landing/ConsumerWaitlistForm";

const IMG = {
  hair: "https://images.unsplash.com/photo-1650468770060-bce89d718bdf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBoYWlyJTIwc2Fsb24lMjBkYXJrJTIwYWVzdGhldGljfGVufDB8fHx8MTc4NTAzOTk2NHww&ixlib=rb-4.1.0&q=85",
  barber: "https://images.pexels.com/photos/7195803/pexels-photo-7195803.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  makeup: "https://images.pexels.com/photos/3886313/pexels-photo-3886313.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  browsLashes: "https://images.pexels.com/photos/3373735/pexels-photo-3373735.jpeg?auto=compress&cs=tinysrgb&w=1200",
  skin: "https://images.pexels.com/photos/29745247/pexels-photo-29745247.jpeg?auto=compress&cs=tinysrgb&w=1200",
  sprayTan: "https://images.pexels.com/photos/3722186/pexels-photo-3722186.jpeg?auto=compress&cs=tinysrgb&w=1200",
  nails: "https://customer-assets-39nsmqrw.emergentagent.net/job_bookaway-hero/artifacts/q7oemhyr_image.png",
  massage: "https://customer-assets-39nsmqrw.emergentagent.net/job_bookaway-hero/artifacts/72p7hafw_image.png",
  wellness: "https://customer-assets-39nsmqrw.emergentagent.net/job_bookaway-hero/artifacts/x0frzyid_image.png",
};

// All nine tiles now have curated imagery.
const CATEGORIES = [
  { name: "Hair", icon: Scissors, span: "md:col-span-2 md:row-span-2", image: IMG.hair },
  { name: "Makeup", icon: Sparkles, span: "md:col-span-2", image: IMG.makeup },
  { name: "Barber", icon: Brush, span: "", image: IMG.barber },
  { name: "Brows & Lashes", icon: Eye, span: "", image: IMG.browsLashes },
  { name: "Skin & Injectables", icon: Syringe, span: "", image: IMG.skin },
  { name: "Spray Tans", icon: SprayCan, span: "", image: IMG.sprayTan },
  { name: "Nails", icon: Palette, span: "", image: IMG.nails },
  { name: "Massage & Body", icon: HeartHandshake, span: "", image: IMG.massage },
  { name: "Wellness", icon: Leaf, span: "", image: IMG.wellness },
];

function CategoryCard({ cat, active, onSelect }) {
  const Icon = cat.icon;
  const hasImage = !!cat.image;
  const isActive = active === cat.name;
  return (
    <motion.button
      type="button"
      variants={fadeUp}
      onClick={() => {
        const next = isActive ? null : cat.name;
        onSelect(next);
        // Only fire on "opening" a category so we don't get double counts
        // when the visitor toggles the tile off.
        if (next) trackCategoryClick(next);
      }}
      data-testid={`category-${cat.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      aria-pressed={isActive}
      className={`group relative min-h-[180px] overflow-hidden rounded-2xl border text-left transition-all duration-300 ${
        isActive
          ? "border-gold shadow-[0_0_0_2px_rgba(201,168,95,0.35)]"
          : "border-black/10 hover:border-gold/60"
      } ${hasImage ? "" : "bg-[#EDEAE4]"} ${cat.span}`}
    >
      {hasImage && (
        <>
          <img
            src={cat.image}
            alt={cat.name}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        </>
      )}
      <div className="relative z-10 flex h-full flex-col justify-between p-6">
        <Icon className="h-6 w-6 text-gold transition-transform duration-500 group-hover:-translate-y-1" />
        <div>
          <h3
            className={`font-serif text-2xl md:text-3xl ${
              hasImage ? "text-white" : "text-[#2A2724]"
            }`}
          >
            {cat.name}
          </h3>
          <p
            className={`mt-1 text-xs transition-opacity duration-500 ${
              isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            } ${hasImage ? "text-white/80" : "text-[#6B6B6B]"}`}
          >
            {isActive ? "Filtering the map ↓" : "Browse last-minute openings →"}
          </p>
        </div>
      </div>
    </motion.button>
  );
}

// Pin dataset — every service category now has representative openings so
// the map filter reflects the real category list.
const MAP_PINS = [
  { top: "22%", left: "28%", category: "Hair",             label: "Hair · 3:30pm" },
  { top: "48%", left: "62%", category: "Brows & Lashes",   label: "Brows · 4:00pm" },
  { top: "66%", left: "38%", category: "Spray Tans",       label: "Spray Tan · Now" },
  { top: "34%", left: "78%", category: "Makeup",           label: "Makeup · 5:15pm" },
  { top: "58%", left: "18%", category: "Barber",           label: "Barber · 3:45pm" },
  { top: "18%", left: "56%", category: "Skin & Injectables", label: "Skin · 6:00pm" },
  { top: "74%", left: "68%", category: "Nails",            label: "Nails · Now" },
  { top: "40%", left: "42%", category: "Massage & Body",   label: "Massage · 4:30pm" },
  { top: "82%", left: "52%", category: "Wellness",         label: "Wellness · 5:00pm" },
];

function MapDiscovery({ activeCategory, onClear }) {
  const visible = activeCategory
    ? MAP_PINS.filter((p) => p.category === activeCategory)
    : MAP_PINS;
  return (
    <div
      data-testid="map-discovery"
      data-active-category={activeCategory || ""}
      className="relative aspect-[4/5] w-full overflow-hidden rounded-3xl border border-black/10 bg-[#EDEAE4] md:aspect-auto md:h-full"
    >
      {/* Grid streets */}
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(212,175,55,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.7) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      {/* Diagonal accent roads */}
      <div className="absolute -left-10 top-1/3 h-px w-[140%] rotate-[18deg] bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <div className="absolute -left-10 top-2/3 h-px w-[140%] -rotate-[10deg] bg-gradient-to-r from-transparent via-black/10 to-transparent" />

      {/* Filter chip — only shown when a category is active */}
      {activeCategory && (
        <button
          type="button"
          data-testid="map-filter-clear"
          onClick={onClear}
          className="absolute left-4 top-4 z-20 inline-flex items-center gap-2 rounded-full border border-gold/60 bg-[#F7F5F2]/90 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-[#2A2724] backdrop-blur-md transition-colors hover:bg-gold hover:text-black"
        >
          <span className="text-gold">Filter</span> {activeCategory}
          <span aria-hidden className="ml-1">×</span>
        </button>
      )}

      {/* Pins */}
      {visible.map((p, i) => (
        <div
          key={`${p.category}-${i}`}
          className="absolute"
          style={{ top: p.top, left: p.left }}
          data-testid={`map-pin-${p.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          <div className="relative">
            <span className="pin-pulse absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full" />
            <MapPin className="relative h-6 w-6 -translate-x-1/2 -translate-y-full text-gold drop-shadow-[0_2px_6px_rgba(212,175,55,0.5)]" fill="#D4AF37" />
          </div>
        </div>
      ))}

      {/* Glass nearby card */}
      <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-black/10 bg-[#F7F5F2]/85 p-5 shadow-[0_16px_50px_-20px_rgba(0,0,0,0.25)] backdrop-blur-xl">
        <div className="mb-3 flex items-center gap-2 text-gold">
          <Clock className="h-4 w-4" />
          <span className="overline !text-gold">
            {activeCategory
              ? `${visible.length} ${activeCategory} openings nearby`
              : "Open now · 0.4 mi away"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-serif text-xl text-[#2A2724]">Maison Noir Studio</p>
            <div className="mt-1 flex items-center gap-1 text-xs text-[#6B6B6B]">
              <Star className="h-3 w-3 text-gold" fill="currentColor" /> 4.9 · Balayage · 3:30pm
            </div>
          </div>
          <span className="rounded-full bg-gold px-4 py-2 text-xs font-medium text-black">
            Book
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Consumers() {
  const [activeCategory, setActiveCategory] = useState(null);
  return (
    <section
      id="consumers"
      data-testid="consumers-section"
      className="relative z-10 bg-[#F7F5F2] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="overline mb-6">For You</p>
            <motion.h2
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#2A2724]"
            >
              Book your look, <span className="italic text-gold">last minute.</span>
            </motion.h2>
          </div>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="max-w-sm text-[#6B6B6B] leading-relaxed"
          >
            Real openings, near you, right now. Find a chair, tap to book, and walk in
            looking your best — no waitlists, no phone calls.
          </motion.p>
        </div>

        <div id="categories" className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Category bento */}
          <motion.div
            variants={stagger(0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="grid grid-cols-2 gap-4 md:grid-cols-4 md:auto-rows-[180px] lg:col-span-7"
          >
            {CATEGORIES.map((c) => (
              <CategoryCard
                key={c.name}
                cat={c}
                active={activeCategory}
                onSelect={setActiveCategory}
              />
            ))}
          </motion.div>

          {/* Map discovery */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={viewportOnce}
            transition={{ duration: 0.9, ease: EASE }}
            className="lg:col-span-5"
          >
            <div className="flex h-full flex-col">
              <div className="mb-5 flex items-center gap-3">
                <MapPin className="h-5 w-5 text-gold" />
                <p className="overline">Map discovery</p>
              </div>
              <div className="min-h-[420px] flex-1">
                <MapDiscovery
                  activeCategory={activeCategory}
                  onClear={() => setActiveCategory(null)}
                />
              </div>
              <button
                data-testid="consumers-download-btn"
                onClick={() => goCustomer()}
                className="mt-6 w-full rounded-full bg-gold py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-[#AA8627]"
              >
                Explore openings near you
              </button>
              <ConsumerWaitlistForm />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
